import {
    Box,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Badge,
    Flex,
    Heading,
    HStack,
    VStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Icon,
    IconButton,
    Tooltip,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Collapse,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverArrow,
    useToast,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { TbCheck, TbChevronDown, TbCircleX, TbClock, TbFileDescription, TbFlag, TbQuote, TbSearch, TbStack2, TbX } from 'react-icons/tb';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import React, { Fragment, useState, useContext, useMemo } from 'react';
import { QuotingTab, OrderDetailResponse, QuotePart } from '../../hooks/useGetOrderDetail';
import SourceBadge from '../SourceBadge';
import SessionContext from '../../contexts/SessionContext';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateOrderDetails } from '../../hooks/useUpdateOrderDetails';
import { formatPrice } from '../../functions';
import PartType from '../PartType';
import PartNumber from '../PartNumber';
import QuotingTag from '../QuotingTag';
import PartBayButton from '../PartBayButton';
import { useCancelQuoteRequest } from '../../hooks/useCancelQuoteRequest';
import { useCloseQuoteRequest } from '../../hooks/useCloseQuoteRequest';
import EMSDetailModal from '../../modals/EMSDetailModal';

const DELIVERED_STATUS_META: Record<
    'fetched' | 'pending' | 'failed',
    { icon: React.ElementType; color: string; label: string; }
> = {
    fetched: { icon: TbCheck as React.ElementType, color: 'green.500', label: 'Fetched' },
    pending: { icon: TbClock as React.ElementType, color: 'orange.400', label: 'Pending' },
    failed: { icon: TbX as React.ElementType, color: 'red.500', label: 'Failed' },
};

interface QuotingTabsProps {
    tabs: Array<QuotingTab>;
    orderId: string;
    vin: string;
    refetch: () => void | Promise<unknown>;
    /**
     * When true, skip the tab bar and render the stacked "All" quotes view
     * (used by quoting fullscreen). Search UI is owned by the parent.
     */
    stackedOnly?: boolean;
    /** Controlled search (used with stackedOnly / fullscreen). */
    searchQuery?: string;
    onSearchQueryChange?: (query: string) => void;
}

const getSortedQuoteTabs = (tabs: Array<QuotingTab>) =>
    tabs
        .filter((t) => t.title.toLowerCase() !== 'all' && (t.timeQuoted || t.scheduled))
        .sort((a, b) => {
            const timeA = a.scheduled
                ? new Date(a.scheduled).getTime()
                : (a.timeQuoted ? new Date(a.timeQuoted).getTime() : 0);
            const timeB = b.scheduled
                ? new Date(b.scheduled).getTime()
                : (b.timeQuoted ? new Date(b.timeQuoted).getTime() : 0);
            return timeB - timeA;
        });

const QuotingTabs = ({
    tabs,
    orderId,
    vin,
    refetch,
    stackedOnly = false,
    searchQuery: controlledSearchQuery,
    onSearchQueryChange,
}: QuotingTabsProps) => {
    const sessionContext = useContext(SessionContext);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const updateOrderDetails = useUpdateOrderDetails();
    const cancelQuoteRequest = useCancelQuoteRequest();
    const queryClient = useQueryClient();

    const [internalSearchQuery, setInternalSearchQuery] = useState('');
    const isSearchControlled = controlledSearchQuery !== undefined && onSearchQueryChange !== undefined;
    const searchQuery = isSearchControlled ? controlledSearchQuery : internalSearchQuery;
    const setSearchQuery = isSearchControlled ? onSearchQueryChange : setInternalSearchQuery;
    const currentTab = useMemo(() => tabs[selectedTabIndex], [tabs, selectedTabIndex]);
    const stackedQuotes = useMemo(() => getSortedQuoteTabs(tabs), [tabs]);

    const handleConfirmDisableAutoOrder = () => {
        updateOrderDetails.mutate({ orderId, allowAutoOrder: false }, {
            onSuccess: () => {
                queryClient.setQueryData<OrderDetailResponse>(['/order/detail', orderId], (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        quoting: old.quoting.map((q: QuotingTab) =>
                            q.key === currentTab.key ? { ...q, allowAutoOrder: false } : q
                        ),
                    };
                });
                setIsConfirmOpen(false);
            },
        });
    };
    const handleCancelQuoteRequest = (quoteRequestId: string) => {
        cancelQuoteRequest.mutate(
            { orderId, quoteRequestId },
            {
                onSuccess: async () => {
                    await refetch();
                },
            },
        );
    };
    if (!tabs || tabs.length === 0) {
        return null;
    }

    const stackedContent = (
        <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
            <VStack spacing={0} align="stretch">
                {stackedQuotes.map((quote) => (
                    <Box key={quote.key}>
                        <QuoteSection
                            quote={quote}
                            includeContainer={false}
                            searchQuery={searchQuery}
                            vin={vin}
                            orderId={orderId}
                            refetch={refetch}
                            isCancelingQuote={cancelQuoteRequest.isPending}
                            onCancelQuoteRequest={handleCancelQuoteRequest}
                        />
                    </Box>
                ))}
            </VStack>
        </Box>
    );

    if (stackedOnly) {
        return stackedContent;
    }

    return (
        <Box>
            <Tabs variant="unstyled" colorScheme="blue" index={selectedTabIndex} onChange={setSelectedTabIndex}>
                <Flex justify="space-between" align="flex-end" mb={4} borderBottom="1.5px solid" borderColor="gray.200" mx={-6} px={6}>
                    <TabList border="none" gap={0}>
                        {tabs.map((tab) => (
                            <Tab
                                key={tab.key}
                                fontWeight="medium"
                                fontSize="sm"
                                color="gray.500"
                                bg="transparent"
                                border="none"
                                borderBottom="1.5px solid"
                                borderColor="transparent"
                                mb="-1.5px"
                                py={2}
                                px={3}
                                _selected={{
                                    color: "gray.800",
                                    borderColor: "gray.500",
                                }}
                                _hover={{
                                    color: "gray.700",
                                }}
                            >
                                <HStack spacing={0}>
                                    <Text>{tab.title}</Text>
                                    {tab.total > 0 && (
                                        <Flex
                                            ml={1.5}
                                            bg="gray.100"
                                            borderRadius="lg"
                                            h="18px"
                                            minW="18px"
                                            px="5px"
                                            align="center"
                                            justify="center"
                                            flexShrink={0}
                                        >
                                            <Text
                                                fontSize="11px"
                                                fontWeight="bold"
                                                lineHeight="1"
                                                color="gray.800"
                                            >
                                                {tab.total}
                                            </Text>
                                        </Flex>
                                    )}
                                </HStack>
                            </Tab>
                        ))}
                    </TabList>
                    <HStack spacing={2} pb={2}>
                        <InputGroup size="sm" w="350px">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3.5} />
                            </InputLeftElement>
                            <Input
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                borderRadius="md"
                                borderColor="gray.300"
                                _hover={{ borderColor: 'gray.400' }}
                                _focus={{ borderColor: '#4051B5', boxShadow: '0 0 0 1px #4051B5' }}
                                fontSize="sm"
                            />
                        </InputGroup>
                        {
                            currentTab?.allowAutoOrder &&
                            currentTab?.status === 'open' &&
                            sessionContext?.isAuthenticated &&
                            sessionContext.permissions.includes('place-order') &&
                            sessionContext.permissions.includes('order-flagged-parts') && (
                                <PartBayButton
                                    colorScheme="partbay-blue"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsConfirmOpen(true)}
                                    text="Disable Auto Order"
                                />
                            )}
                    </HStack>
                </Flex>

                <TabPanels>
                    {tabs.map((tab) => {
                        // Check if this is the "All" tab
                        const isAllTab = tab.title.toLowerCase() === 'all';

                        if (isAllTab) {
                            return (
                                <TabPanel key={tab.key} px={0} py={0}>
                                    {stackedContent}
                                </TabPanel>
                            );
                        }

                        // Regular quote tab (Quote 1, Quote 2, etc.)
                        return (
                            <TabPanel key={tab.key} px={0} py={0}>
                                <QuoteSection
                                    quote={tab}
                                    searchQuery={searchQuery}
                                    vin={vin}
                                    orderId={orderId}
                                    refetch={refetch}
                                    isCancelingQuote={cancelQuoteRequest.isPending}
                                    onCancelQuoteRequest={handleCancelQuoteRequest}
                                />
                            </TabPanel>
                        );
                    })}
                </TabPanels>
            </Tabs>

            <Modal isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} size="md" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Disable Auto Order</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text color="gray.700">
                            Are you sure you want to disable auto ordering for this quote? Parts will no longer be automatically ordered.
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <HStack spacing={3}>
                            <Button
                                size="action"
                                variant="outlineGray"
                                onClick={() => setIsConfirmOpen(false)}
                            >
                                Cancel
                            </Button>
                            <PartBayButton
                                size="action"
                                colorScheme="partbay-blue"
                                variant="solid"
                                onClick={handleConfirmDisableAutoOrder}
                                isLoading={updateOrderDetails.isPending}
                                loadingText="Disabling..."
                                text="Confirm"
                            />
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

interface AssemblyIndicatorReadOnlyProps {
    part: QuotePart;
}

const AssemblyIndicatorReadOnly = ({ part }: AssemblyIndicatorReadOnlyProps) => {
    const isParent = !!part.assemblySubParts && part.assemblySubParts.length > 0;
    const color = part.assemblyColor || 'gray.500';
    const label = isParent
        ? 'This part is the parent of an assembly mapping.'
        : 'Part of an assembly';

    return (
        <Tooltip label={label} placement="top" hasArrow>
            <Box as="span" display="inline-flex" alignItems="center">
                <Icon as={TbStack2 as React.ElementType} w={4} h={4} color={color} />
            </Box>
        </Tooltip>
    );
};

interface QuoteSectionProps {
    quote: QuotingTab;
    includeContainer?: boolean;
    searchQuery: string;
    vin: string;
    orderId: string;
    refetch: () => void | Promise<unknown>;
    isCancelingQuote: boolean;
    onCancelQuoteRequest: (quoteRequestId: string) => void;
}

const startOfLocalDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const formatClockTime = (d: Date) =>
    d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();

/** Today → time only (or "today" if date-only). Tomorrow → tmr. This week → Mon/Tue. This year → m/d. Else m/d/yy. */
const formatQuoteWhen = (value: Date | string, withTime: boolean): string => {
    const date = new Date(value);
    const today = startOfLocalDay(new Date());
    const day = startOfLocalDay(date);
    const diffDays = Math.round((day.getTime() - today.getTime()) / 86_400_000);
    const time = formatClockTime(date);

    let datePart: string | null;
    if (diffDays === 0) {
        datePart = null;
    } else if (diffDays === 1) {
        datePart = 'Tmr.';
    } else if (diffDays !== 0 && Math.abs(diffDays) < 7) {
        datePart = date.toLocaleDateString('en-US', { weekday: 'short' });
    } else if (date.getFullYear() === today.getFullYear()) {
        datePart = `${date.getMonth() + 1}/${date.getDate()}`;
    } else {
        datePart = `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(-2)}`;
    }

    if (!withTime) {
        return datePart ?? 'today';
    }
    return datePart ? `${datePart} ${time}` : time;
};

const formatEstReturnTime = (time: Date | string | null | undefined) => {
    if (!time) {
        return null;
    }
    return `Est. Return ${formatQuoteWhen(time, true)}`;
};

const QuoteSection = ({
    quote,
    includeContainer = true,
    searchQuery,
    vin,
    orderId,
    refetch,
    isCancelingQuote,
    onCancelQuoteRequest,
}: QuoteSectionProps) => {
    const [expandedPartIds, setExpandedPartIds] = useState<Set<string>>(new Set());
    const [isEmsModalOpen, setIsEmsModalOpen] = useState(false);
    const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
    const closeQuoteRequest = useCloseQuoteRequest();
    const toast = useToast();
    const sessionContext = useContext(SessionContext);
    const canCloseQuotes = !!sessionContext?.isAuthenticated && sessionContext.permissions.includes('request-quotes');

    const filteredParts = useMemo(() => {
        if (!searchQuery.trim()) return quote.parts;
        const q = searchQuery.toLowerCase().trim();
        return quote.parts.filter((part) =>
            part.name.toLowerCase().includes(q) ||
            (part.partNumber && part.partNumber.toLowerCase().includes(q)) ||
            (part.type && part.type.toLowerCase().includes(q))
        );
    }, [quote.parts, searchQuery]);

    const statusStyles = quote.status === 'open'
        ? { bg: 'green.50', color: 'green.800', borderColor: 'green.200' }
        : quote.status === 'pending'
            ? { bg: 'orange.50', color: 'orange.800', borderColor: 'orange.200' }
            : { bg: 'red.50', color: 'red.800', borderColor: 'red.200' };
    const displayDate = quote.scheduled ?? quote.timeQuoted;
    const quoteMetaLine = (() => {
        const parts: string[] = [];
        if (quote.scheduled) {
            parts.push(`Runs ${formatQuoteWhen(quote.scheduled, false)}`);
            if (quote.scheduledArrivalDate) {
                parts.push(`Arrival ${formatQuoteWhen(quote.scheduledArrivalDate, false)}`);
            }
        } else if (displayDate) {
            parts.push(`Quoted at ${formatQuoteWhen(displayDate, true)}`);
            if (quote.status !== 'pending' && quote.quotedMinutes) {
                parts.push(`${quote.quotedMinutes} min`);
            }
            if (quote.timesExtended && quote.timesExtended > 0) {
                parts.push(`Extended ${quote.timesExtended}×`);
            }
        }
        if (quote.quotedBy) parts.push(quote.quotedBy);
        if (!quote.scheduled && quote.status !== 'pending') {
            const estReturn = formatEstReturnTime(quote.estReturnTime);
            if (estReturn) parts.push(estReturn);
        }
        return parts.length ? parts.join('  ·  ') : null;
    })();

    const handleConfirmCloseQuotes = () => {
        closeQuoteRequest.mutate(
            { orderId, quoteRequestId: quote.key },
            {
                onSuccess: async () => {
                    setIsCloseConfirmOpen(false);
                    await refetch();
                },
                onError: (error: any) => {
                    toast({
                        title: 'Error closing quotes',
                        description: error?.response?.data?.message || error.message || 'Something went wrong.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                        position: 'bottom-right',
                    });
                },
            },
        );
    };

    const toggleExpandedPart = (partId: string) => {
        setExpandedPartIds((prev) => {
            const next = new Set(prev);
            if (next.has(partId)) {
                next.delete(partId);
            } else {
                next.add(partId);
            }
            return next;
        });
    };

    const allVisiblePartsExpanded = filteredParts.length > 0 && filteredParts.every((part) => expandedPartIds.has(part._id));

    const toggleAllExpandedParts = () => {
        setExpandedPartIds((prev) => {
            const next = new Set(prev);
            if (allVisiblePartsExpanded) {
                filteredParts.forEach((part) => next.delete(part._id));
            } else {
                filteredParts.forEach((part) => next.add(part._id));
            }
            return next;
        });
    };

    const content = (
        <>
            {displayDate && (
                <Box px={6} pt={5} pb={4}>
                    <Flex align="center" gap={3}>
                        <HStack spacing={2} align="center" flexShrink={0}>
                            <Heading size="md" color="gray.800">
                                {quote.title}
                            </Heading>
                            {quote.emsId && (
                                <Tooltip label="View submitted EMS" hasArrow>
                                    <IconButton
                                        aria-label="View submitted EMS"
                                        icon={<Icon as={TbFileDescription as React.ElementType} boxSize={4} />}
                                        size="xs"
                                        variant="ghost"
                                        color="blue.500"
                                        onClick={() => setIsEmsModalOpen(true)}
                                    />
                                </Tooltip>
                            )}
                            {quote.preOrder !== undefined && (
                                <QuotingTag
                                    tag={quote.preOrder ? 'pre-order' : 'on-site'}
                                    text={quote.preOrder ? 'PRE-ORDER' : 'ON-SITE'}
                                />
                            )}
                            {quote.allowAutoOrder && (
                                <QuotingTag
                                    tag="auto-order"
                                    text="AUTO ORDER"
                                />
                            )}
                            {quote.expedited && (
                                <QuotingTag
                                    tag="expedited"
                                    text="EXPEDITED"
                                />
                            )}
                            {quote.oemEndoresement && (
                                <QuotingTag
                                    tag="oem-endorsement"
                                    text="OEM ENDORSEMENT"
                                />
                            )}
                        </HStack>
                        {quoteMetaLine && (
                            <Text
                                flex={1}
                                minW={0}
                                fontSize="sm"
                                color="gray.500"
                                noOfLines={1}
                                textAlign="right"
                            >
                                {quoteMetaLine}
                            </Text>
                        )}
                        <HStack spacing={2} align="center" flexShrink={0} ml={quoteMetaLine ? 0 : 'auto'}>
                            {quote.scheduled && quote.status === 'pending' && (
                                <Button
                                    colorScheme="red"
                                    variant="outline"
                                    size="xs"
                                    isLoading={isCancelingQuote}
                                    loadingText="Canceling..."
                                    onClick={() => onCancelQuoteRequest(quote.key)}
                                >
                                    Cancel Request
                                </Button>
                            )}
                            {quote.status && (() => {
                                const statusBadge = (
                                    <Badge
                                        px={2.5}
                                        py={0.5}
                                        borderRadius="full"
                                        variant="solid"
                                        bg={statusStyles.bg}
                                        color={statusStyles.color}
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        textTransform="capitalize"
                                        borderWidth="1px"
                                        borderColor={statusStyles.borderColor}
                                        cursor="default"
                                    >
                                        {quote.status}
                                    </Badge>
                                );

                                if (!quote.delivered || quote.delivered.length === 0) {
                                    return statusBadge;
                                }

                                return (
                                    <Popover trigger="hover" placement="bottom-end" isLazy>
                                        <PopoverTrigger>{statusBadge}</PopoverTrigger>
                                        <PopoverContent w="auto" maxW="260px">
                                            <PopoverArrow />
                                            <PopoverBody>
                                                <VStack align="stretch" spacing={2}>
                                                    {quote.delivered.map((entry, index) => {
                                                        const statusMeta = DELIVERED_STATUS_META[entry.status];
                                                        return (
                                                            <HStack key={`${entry.markteplace}-${index}`} spacing={2} align="center" justify="space-between">
                                                                <HStack spacing={2} align="center" minW={0}>
                                                                    <SourceBadge source={entry.markteplace} />
                                                                    {entry.supplier && (
                                                                        <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                                                            {entry.supplier}
                                                                        </Text>
                                                                    )}
                                                                </HStack>
                                                                {statusMeta && (
                                                                    <Icon as={statusMeta.icon} color={statusMeta.color} boxSize={4} flexShrink={0} />
                                                                )}
                                                            </HStack>
                                                        );
                                                    })}
                                                </VStack>
                                            </PopoverBody>
                                        </PopoverContent>
                                    </Popover>
                                );
                            })()}
                            {canCloseQuotes && (
                                <Menu placement="bottom-end">
                                    <MenuButton
                                        as={IconButton}
                                        aria-label="Options"
                                        icon={<Icon as={HiOutlineDotsVertical as React.ElementType} w={5} h={5} />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="gray"
                                        borderRadius="lg"
                                        _hover={{ bg: 'gray.50' }}
                                        _active={{ bg: 'gray.100' }}
                                        _focus={{ boxShadow: 'outline' }}
                                    />
                                    <MenuList py={1} minW="200px">
                                        <MenuItem
                                            onClick={() => setIsCloseConfirmOpen(true)}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: '#E8EAF6', color: '#4051B5' }}
                                            isDisabled={quote.status !== 'open' || closeQuoteRequest.isPending}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={TbCircleX as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>Close Quotes</Text>
                                            </HStack>
                                        </MenuItem>
                                    </MenuList>
                                </Menu>
                            )}
                        </HStack>
                    </Flex>
                </Box>
            )}

            {quote.emsId && (
                <EMSDetailModal
                    isOpen={isEmsModalOpen}
                    onClose={() => setIsEmsModalOpen(false)}
                    emsId={quote.emsId}
                />
            )}

            <Modal isOpen={isCloseConfirmOpen} onClose={() => setIsCloseConfirmOpen(false)} size="md" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Close Quotes</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text color="gray.700">
                            Are you sure you want to close quotes? This can't be undone and you'll have to re-quote if you don't see enough quotes.
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <HStack spacing={3}>
                            <Button
                                size="action"
                                variant="outlineGray"
                                onClick={() => setIsCloseConfirmOpen(false)}
                                isDisabled={closeQuoteRequest.isPending}
                            >
                                Cancel
                            </Button>
                            <PartBayButton
                                size="action"
                                colorScheme="partbay-blue"
                                variant="solid"
                                onClick={handleConfirmCloseQuotes}
                                isLoading={closeQuoteRequest.isPending}
                                loadingText="Closing..."
                                text="Close Quotes"
                            />
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Box borderTop="1px solid" borderColor="gray.200" overflowX="auto">
                <Table
                    variant="simple"
                    size="sm"
                    w="100%"
                    minW="700px"
                    sx={{
                        tableLayout: 'fixed',
                        'th:first-of-type, td:first-of-type': { pl: 6 },
                        'th:last-of-type, td:last-of-type': { pr: 6 },
                        'tr.estimate-detail-row td:first-of-type': { pl: '0 !important' },
                        'tr.estimate-detail-row td:last-of-type': { pr: '0 !important' },
                    }}
                >
                    <colgroup>
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '36%' }} />
                        <col style={{ width: '21%' }} />
                        <col style={{ width: '15%' }} />
                    </colgroup>
                    <Thead>
                        <Tr bg="gray.50">
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} minW="70px">
                                <HStack spacing={1.5}>
                                    <Tooltip label={allVisiblePartsExpanded ? 'Collapse all estimates' : 'Expand all estimates'} placement="top" hasArrow>
                                        <Box
                                            as="button"
                                            type="button"
                                            aria-label={allVisiblePartsExpanded ? 'Collapse all estimates' : 'Expand all estimates'}
                                            onClick={toggleAllExpandedParts}
                                            display="inline-flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            w="18px"
                                            h="18px"
                                            color="gray.500"
                                            borderRadius="sm"
                                            transition="all 0.15s ease"
                                            _hover={{ bg: 'gray.100', color: 'gray.700' }}
                                        >
                                            <Icon
                                                as={TbChevronDown as React.ElementType}
                                                w={4}
                                                h={4}
                                                transform={allVisiblePartsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                                                transition="transform 0.18s ease"
                                            />
                                        </Box>
                                    </Tooltip>
                                    <Text as="span" fontSize="xs" fontWeight="semibold">
                                        LINE #
                                    </Text>
                                </HStack>
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} minW="60px">
                                QTY
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} minW="100px">
                                PART TYPE
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} minW="200px">
                                DESCRIPTION
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} minW="140px">
                                OE PART #
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} minW="100px" textAlign="right">
                                PRICE
                            </Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {filteredParts.map((part, index) => {
                            const isExpanded = expandedPartIds.has(part._id);
                            return (
                                <Fragment key={part._id}>
                                    <Tr
                                        bg={part.quoteAndExclude ? "red.50" : part.flag ? "orange.50" : undefined}
                                    >
                                        <Td py={4} px={4}>
                                            <HStack spacing={1.5}>
                                                <Tooltip label="Estimate" placement="top" hasArrow isDisabled={isExpanded}>
                                                    <Box
                                                        as="button"
                                                        type="button"
                                                        aria-label={isExpanded ? 'Collapse estimate details' : 'Expand estimate details'}
                                                        onClick={() => toggleExpandedPart(part._id)}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        w="18px"
                                                        h="18px"
                                                        color="gray.500"
                                                        borderRadius="sm"
                                                        transition="all 0.15s ease"
                                                        _hover={{ bg: 'gray.100', color: 'gray.700' }}
                                                    >
                                                        <Icon
                                                            as={TbChevronDown as React.ElementType}
                                                            w={4}
                                                            h={4}
                                                            transform={isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                                                            transition="transform 0.18s ease"
                                                        />
                                                    </Box>
                                                </Tooltip>
                                                <Text fontWeight="medium" color="gray.900">
                                                    {index + 1}
                                                </Text>
                                                {part.flag && (
                                                    <Tooltip
                                                        label={part.flagReason || 'Flagged'}
                                                        placement="top"
                                                        hasArrow
                                                    >
                                                        <Box as="span" display="inline-flex">
                                                            <Icon
                                                                as={TbFlag as React.ElementType}
                                                                w={3.5}
                                                                h={3.5}
                                                                color="orange.500"
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                {(part.assemblyColor || (part.assemblySubParts && part.assemblySubParts.length > 0)) && (
                                                    <AssemblyIndicatorReadOnly part={part} />
                                                )}
                                                {part.quoteAndExclude && (
                                                    <Tooltip
                                                        label="Excluded from ordering because it was marked to only quote the part"
                                                        placement="top"
                                                        hasArrow
                                                    >
                                                        <Box as="span" display="inline-flex">
                                                            <Icon
                                                                as={TbQuote as React.ElementType}
                                                                w={4}
                                                                h={4}
                                                                color="red.500"
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </HStack>
                                        </Td>
                                        <Td py={4} px={4}>
                                            <Text color="gray.900">
                                                {part.quantity || 1}
                                            </Text>
                                        </Td>
                                        <Td py={4} px={4}>
                                            {part.type ? (
                                                <PartType
                                                    partType={part.type}
                                                />
                                            ) : null}
                                        </Td>
                                        <Td py={4} px={4}>
                                            <Text color="gray.900" fontWeight="medium">
                                                {part.name}
                                            </Text>
                                        </Td>
                                        <Td py={4} px={4}>
                                            <PartNumber
                                                partNumber={part.partNumber}
                                                vin={vin}
                                            />
                                        </Td>
                                        <Td py={4} px={4} textAlign="right">
                                            <Text color="gray.900" fontWeight="medium">
                                                {formatPrice(part.quotedListPrice)}
                                            </Text>
                                        </Td>
                                    </Tr>
                                    <Tr className="estimate-detail-row" bg={isExpanded ? 'gray.50' : undefined}>
                                        <Td p={0} borderTop="none" borderBottom={isExpanded ? undefined : 'none'}>
                                            <Collapse in={isExpanded} animateOpacity>
                                                <Box px={4} pl={6} h="32px" display="flex" alignItems="center">
                                                    <HStack spacing={1.5}>
                                                        <Box w="18px" h="18px" flexShrink={0} />
                                                        <Text color="gray.700" fontWeight="medium">
                                                            {part.lineNumber}
                                                        </Text>
                                                    </HStack>
                                                </Box>
                                            </Collapse>
                                        </Td>
                                        <Td p={0} borderTop="none" borderBottom={isExpanded ? undefined : 'none'}>
                                            <Collapse in={isExpanded} animateOpacity>
                                                <Box px={4} h="32px" />
                                            </Collapse>
                                        </Td>
                                        <Td p={0} borderTop="none" borderBottom={isExpanded ? undefined : 'none'}>
                                            <Collapse in={isExpanded} animateOpacity>
                                                <Box px={4} h="32px" display="flex" alignItems="center">
                                                    {part.estimatePartType ? (
                                                        <PartType partType={part.estimatePartType} />
                                                    ) : (
                                                        <Text fontSize="sm" color="gray.500">N/A</Text>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </Td>
                                        <Td p={0} borderTop="none" borderBottom={isExpanded ? undefined : 'none'}>
                                            <Collapse in={isExpanded} animateOpacity>
                                                <Box px={4} h="32px" />
                                            </Collapse>
                                        </Td>
                                        <Td p={0} borderTop="none" borderBottom={isExpanded ? undefined : 'none'}>
                                            <Collapse in={isExpanded} animateOpacity>
                                                <Box px={4} h="32px" />
                                            </Collapse>
                                        </Td>
                                        <Td p={0} borderTop="none" borderBottom={isExpanded ? undefined : 'none'} textAlign="right">
                                            <Collapse in={isExpanded} animateOpacity>
                                                <Box px={4} pr={6} h="32px" display="flex" alignItems="center" justifyContent="flex-end">
                                                    <Text color="gray.700" fontWeight="medium">
                                                        {formatPrice(part.estimateListPrice)}
                                                    </Text>
                                                </Box>
                                            </Collapse>
                                        </Td>
                                    </Tr>
                                </Fragment>
                            );
                        })}
                    </Tbody>
                </Table>
            </Box>
        </>
    );

    if (includeContainer) {
        return (
            <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                {content}
            </Box>
        );
    }

    return content;
};

export default QuotingTabs;
